// ---------------------------------------------------------------------------
// LA LOGIQUE DE L'ÉCRAN DES POINTS — tout ce qui se prouve sans navigateur
// ---------------------------------------------------------------------------
// Toji, 14/09 : « dans la section admin, on mette toute la gestion au niveau
// des points, un peu comme ce qu'on a fait pour Roma, mais en simplifié. Il y a
// quand même le système des multiplicateurs […] on peut soit choisir pour un
// épisode, soit choisir une durée […] on peut payer tant et on fait des
// discounts pour des grandes sessions […] s'il y a un rush, les gens peuvent
// l'économiser. Comme ça, ils déclenchent tout dans une soirée. »
//
// ⚠️ CE MODULE N'INVENTE AUCUNE RÈGLE, ET C'EST TOUT SON INTÉRÊT.
//
// Les bornes des réglages vivent dans `reglages-points.js`, le calcul d'un prix
// dans `economie.js` : ce sont les DEUX fichiers que le bot exécute pour de
// vrai quand quelqu'un lance un épisode. Un écran qui referait l'un ou l'autre
// afficherait « 5 épisodes = 120 points » pendant que le bot en débiterait 135,
// et la divergence ne lèverait jamais — elle se verrait un soir, sur un refus
// que personne ne comprend. On IMPORTE donc, on ne recopie pas.
//
// ⚠️ POURQUOI CE FICHIER EXISTE PLUTÔT QUE D'ÊTRE DANS `admin.js`.
//
// `src/admin.js` N'IMPORTE RIEN — c'est une règle du projet, vérifiée par
// `essais/ecran-admin.mjs` : il reçoit ses outils au montage pour rester
// éprouvable sans navigateur. Ce module-ci est donc l'endroit où les imports
// ont le droit d'exister ; `src/main.js` le passe à `monterEcranAdmin` comme
// n'importe quel autre outil.
//
// ⚠️ ET C'EST UN FICHIER QUI TRAVERSE LA FRONTIÈRE DES DEUX APPLICATIONS.
//
// L'image du lecteur ne copie que `apps/player` (voir le Dockerfile) : sans les
// deux lignes `COPY apps/bot/src/…` ajoutées avec ce lot, `npm run build`
// tomberait dans l'étage de construction et `server.js` refuserait de démarrer.
// Les deux pannes sont BRUYANTES, ce qui est exactement ce qu'on veut d'un
// fichier manquant — l'inverse (un artefact absent servi en 404 silencieux) a
// déjà été payé ici.
// ---------------------------------------------------------------------------

import {
  DEFAUTS, MODES, PALIERS_MAX, MULTIPLICATEUR_MAX,
  lirePoints, normaliserPalier, normaliserMultiplicateur,
  facteurDe, appliquerFacteur,
} from '../../bot/src/reglages-points.js';
import { prixDuLot, partsEgales, remiseDuVolume, tranchesPour } from '../../bot/src/economie.js';

export {
  DEFAUTS, MODES, PALIERS_MAX, MULTIPLICATEUR_MAX,
  lirePoints, normaliserPalier, normaliserMultiplicateur,
  facteurDe, appliquerFacteur,
  prixDuLot, partsEgales, remiseDuVolume, tranchesPour,
};

/** Ce que l'écran demande à l'exploitant, mode par mode. */
export const CHAMPS_EPISODE = Object.freeze(['prixEpisode', 'prixFilm']);
export const CHAMPS_DUREE = Object.freeze(['prixTranche', 'minutesParTranche']);

/** Au plus dix multiplicateurs : même raison que les paliers — au-delà, ça ne se lit plus. */
export const MULTIPLICATEURS_MAX = 10;

/**
 * Quels champs de prix l'écran montre, et lesquels il cache.
 *
 * ⚠️ CACHER, PAS EFFACER. Les deux jeux de prix restent dans les réglages : on
 * passe de « à l'épisode » à « à la durée » pour un soir de rush, et repasser
 * ne doit pas demander de ressaisir ce qu'on avait réglé. Un écran qui remet à
 * zéro le mode qu'il masque punit exactement le geste qu'on voulait rendre
 * facile.
 */
export function champsDuMode(mode) {
  const choisi = MODES.includes(String(mode)) ? String(mode) : DEFAUTS.mode;
  const duree = choisi === 'duree';
  return {
    mode: choisi,
    montres: duree ? [...CHAMPS_DUREE] : [...CHAMPS_EPISODE],
    caches: duree ? [...CHAMPS_EPISODE] : [...CHAMPS_DUREE],
    // Le mot que porte une unité dans cette façon de facturer. Il sert partout
    // dans l'aperçu : « 30 minutes de vocal = un épisode » n'a aucun sens quand
    // on facture à l'heure de lecture.
    unite: duree ? 'tranche' : 'épisode',
  };
}

// ---------------------------------------------------------------------------
// LE BROUILLON — ce que l'écran tient PENDANT qu'on tape
// ---------------------------------------------------------------------------
// ⚠️ UNE LIGNE EN COURS DE SAISIE N'EST PAS UNE LIGNE INVALIDE.
//
// `normaliserPaliers` jette un palier dont la remise vaut zéro — c'est juste,
// un palier sans remise ne veut rien dire. Mais une ligne qu'on vient
// d'ajouter, ou dont on efface le chiffre pour le retaper, passe forcément par
// cet état : normaliser à chaque frappe ferait DISPARAÎTRE la ligne sous les
// doigts. Le brouillon garde donc les valeurs telles quelles (chaînes
// comprises) et ne se normalise qu'au moment de calculer un aperçu ou
// d'enregistrer.
// ---------------------------------------------------------------------------

/** Le brouillon de départ : des réglages normalisés, rendus éditables. */
export function brouillonDe(brut) {
  const r = lirePoints(brut);
  return {
    ...r,
    paliers: r.paliers.map((p) => ({ quantite: p.quantite, remisePourcent: p.remisePourcent })),
    multiplicateurs: r.multiplicateurs.map((m) => ({ role: m.role, facteur: m.facteur, nom: m.nom ?? '' })),
  };
}

/**
 * Une ligne de plus dans la grille de volume.
 *
 * Le seuil proposé continue la grille au lieu de repartir de 1 : deux lignes du
 * même seuil se dédoublonnent à l'enregistrement, et l'exploitant croirait
 * avoir ajouté une ligne qui ne sera jamais là.
 */
export function ajouterPalier(paliers = []) {
  const liste = Array.isArray(paliers) ? paliers : [];
  if (liste.length >= PALIERS_MAX) return [...liste];
  const dernier = liste[liste.length - 1];
  const seuil = Math.max(2, Math.floor(Number(dernier?.quantite) || 1) + 1);
  const remise = Math.min(100, Math.max(5, Math.floor(Number(dernier?.remisePourcent) || 0) + 5));
  return [...liste, { quantite: seuil, remisePourcent: remise }];
}

/**
 * Une ligne de plus dans les multiplicateurs.
 *
 * Le rôle part VIDE : proposer le premier rôle de la liste poserait un
 * multiplicateur sur quelqu'un qu'on n'a pas choisi, et un facteur qui
 * s'applique sans qu'on l'ait voulu ne se remarque pas.
 */
export function ajouterMultiplicateur(multiplicateurs = []) {
  const liste = Array.isArray(multiplicateurs) ? multiplicateurs : [];
  if (liste.length >= MULTIPLICATEURS_MAX) return [...liste];
  return [...liste, { role: '', facteur: 1.5, nom: '' }];
}

/** Retire une ligne. Un index hors de la liste ne retire rien — et ne lève pas. */
export function retirerLigne(liste = [], index) {
  const src = Array.isArray(liste) ? liste : [];
  const i = Math.floor(Number(index));
  if (!Number.isFinite(i) || i < 0 || i >= src.length) return [...src];
  return src.filter((_, k) => k !== i);
}

/** Pose une valeur dans une ligne, telle qu'elle a été tapée. */
export function changerLigne(liste = [], index, champ, valeur) {
  const src = Array.isArray(liste) ? liste : [];
  const i = Math.floor(Number(index));
  if (!Number.isFinite(i) || i < 0 || i >= src.length) return [...src];
  return src.map((ligne, k) => (k === i ? { ...ligne, [champ]: valeur } : { ...ligne }));
}

/**
 * Ce que l'écran ENVOIE — et il passe par la même porte que le bot.
 *
 * ⚠️ C'EST ICI QUE L'ALLER-RETOUR SE JOUE. Le serveur renormalise de son côté
 * (il ne fait confiance à aucun corps de requête), mais s'il devait corriger
 * quelque chose, l'écran afficherait autre chose que ce qui a été enregistré
 * jusqu'au prochain rechargement. En normalisant AVANT d'envoyer, ce qu'on voit
 * est ce qui part, et ce qui part est ce qui revient.
 */
export function reglagesDuBrouillon(brouillon) {
  return lirePoints(brouillon);
}

/** Le nom lisible d'un rôle, ou son identifiant quand Discord ne l'a pas dit. */
export function nomDuRole(id, roles = []) {
  const cherche = String(id ?? '');
  if (!cherche) return '';
  const trouve = (Array.isArray(roles) ? roles : []).find((r) => String(r?.id) === cherche);
  return trouve?.nom ? String(trouve.nom) : cherche;
}

// ---------------------------------------------------------------------------
// L'APERÇU — un barème ne se comprend qu'en chiffres, pas en champs
// ---------------------------------------------------------------------------
// ⚠️ SANS CETTE PARTIE, L'ÉCRAN NE SERAIT QU'UN FORMULAIRE.
//
// « prixEpisode: 30 » et « parMinuteVocale: 1 » ne disent RIEN tant qu'on ne les
// a pas multipliés l'un par l'autre dans sa tête. Le document de conception
// résume tout le barème en une phrase — « 30 points = 30 minutes de vocal = un
// épisode » — et c'est cette phrase-là qu'il faut recalculer sous les doigts, à
// chaque frappe. Un exploitant qui triple le prix doit VOIR la soirée passer de
// deux heures à six, sans avoir à la tester sur ses invités.
//
// Chaque ligne porte ses nombres en plus de sa phrase : le banc éprouve les
// nombres, l'écran affiche la phrase. Un aperçu dont on ne pourrait vérifier que
// le texte serait un aperçu qu'on ne vérifie pas.
// ---------------------------------------------------------------------------

/** « 1 » et non « 1.5 » quand le facteur est rond : « × 2 » se lit, « × 2.00 » non. */
function nombre(n) {
  const v = Number(n) || 0;
  return String(Number.isInteger(v) ? v : Math.round(v * 100) / 100).replace('.', ',');
}

const pluriel = (n, mot) => `${n} ${mot}${n > 1 ? 's' : ''}`;

export function apercuDuBareme(brut, { roles = [] } = {}) {
  const r = lirePoints(brut);

  // ⚠️ L'INTERRUPTEUR SE DIT EN PREMIER ET SEUL. Afficher un barème sous un
  // « coupé » laisserait croire qu'il s'applique quand même — c'est le genre de
  // malentendu qui fait qu'on cherche pendant une heure pourquoi personne n'est
  // débité.
  if (!r.actif) {
    return [{
      cle: 'coupe',
      dit: 'L’économie est COUPÉE : les séances sont libres — n’importe qui ouvre un épisode, '
        + 'rien n’est débité, et les soldes continuent de se remplir en attendant qu’on la rallume.',
    }];
  }

  const { unite } = champsDuMode(r.mode);
  const duree = r.mode === 'duree';
  const minutesDUnLot = (n) => (duree ? n * r.minutesParTranche : null);

  const un = prixDuLot({
    nature: 'episode', quantite: 1, minutes: minutesDUnLot(1),
    participants: 1, boosters: 0, reglages: r,
  });

  const lignes = [];

  // 1. LE CHIFFRE FONDATEUR : combien de temps donné pour une unité regardée.
  if (un.prix <= 0) {
    lignes.push({
      cle: 'gagner', prix: 0, minutes: 0,
      dit: `Une séance ne coûte RIEN (${unite} à 0 point) : le barème est en place mais ne demande jamais rien.`,
    });
  } else if (r.parMinuteVocale > 0) {
    const minutes = Math.ceil(un.prix / r.parMinuteVocale);
    lignes.push({
      cle: 'gagner', prix: un.prix, minutes,
      dit: `${pluriel(minutes, 'minute')} de vocal = ${unite === 'tranche' ? `une tranche de ${r.minutesParTranche} min de lecture` : 'un épisode'} (${un.prix} points).`,
    });
  } else {
    lignes.push({
      cle: 'gagner', prix: un.prix, minutes: null,
      dit: `Le vocal ne rapporte RIEN (0 point la minute) : seuls l’écrit et les crédits d’un propriétaire remplissent les soldes, et ${unite} coûte ${un.prix} points.`,
    });
  }

  // 2. ÊTRE PLUSIEURS COÛTE MOINS CHER — la règle contre-intuitive du document.
  const parts = [...partsEgales(un.prix, ['a', 'b', 'c']).values()];
  if (parts.length === 3) {
    const bas = Math.min(...parts);
    const haut = Math.max(...parts);
    lignes.push({
      cle: 'groupe', part: bas, partHaute: haut, prix: un.prix,
      dit: `À 3 personnes en vocal : ${bas === haut ? `${bas} points chacune` : `${bas} ou ${haut} points chacune`} au lieu de ${un.prix} — le prix se partage, être ensemble coûte moins cher.`,
    });
  }

  // 3. ACHETER GROS COÛTE MOINS CHER À L'UNITÉ — ce qui donne un sens à l'épargne.
  const lot = Math.max(2, Math.floor(Number(r.paliers[0]?.quantite) || 5));
  const gros = prixDuLot({
    nature: 'episode', quantite: lot, minutes: minutesDUnLot(lot),
    participants: 1, boosters: 0, reglages: r,
  });
  lignes.push({
    cle: 'volume', quantite: lot, prix: gros.prix, plein: gros.base, remise: gros.remiseVolume,
    dit: gros.remiseVolume > 0
      ? `${pluriel(lot, unite)} d’un coup : ${gros.prix} points au lieu de ${gros.base} (−${gros.remiseVolume} %) — garder ses points pour une soirée entière rapporte.`
      : `${pluriel(lot, unite)} d’un coup : ${gros.prix} points, soit le plein tarif. Sans palier de volume, épargner ne vaut pas mieux que dépenser au coup par coup.`,
  });

  // 4. LA PORTE DE SERVICE DU BOOST.
  const boost = prixDuLot({
    nature: 'episode', quantite: 1, minutes: minutesDUnLot(1),
    participants: 1, boosters: 1, reglages: r,
  });
  lignes.push({
    cle: 'booster', prix: boost.prix, plein: un.prix, remise: r.remiseBoosterPourcent,
    dit: r.remiseBoosterPourcent > 0
      ? `Un booster seul paie ${boost.prix} points au lieu de ${un.prix} (−${r.remiseBoosterPourcent} %). À plusieurs, la remise ne porte que sur SA part.`
      : `Booster le serveur ne change rien au prix (remise à 0 %).`,
  });

  // 5. LA RÉSERVE — « s'il y a un rush, les gens peuvent l'économiser ».
  if (un.prix > 0) {
    const avance = Math.floor(r.plafond / un.prix);
    lignes.push({
      cle: 'reserve', plafond: r.plafond, avance,
      dit: avance > 0
        ? `Le plafond de ${r.plafond} points met ${pluriel(avance, unite)} d’avance en réserve : de quoi tout déclencher dans une même soirée.`
        : `⚠️ Le plafond de ${r.plafond} points est SOUS le prix d’${unite === 'tranche' ? 'une tranche' : 'un épisode'} (${un.prix}) : personne ne pourra jamais rien lancer seul.`,
    });
  }

  // 6. L'ÉCRIT, ET SON GARDE-FOU. Le délai est ce qui empêche le remplissage.
  const parHeure = r.parMessage * Math.floor(3600 / r.delaiEntreMessagesS);
  lignes.push({
    cle: 'ecrit', parHeure,
    dit: `À l’écrit : ${pluriel(r.parMessage, 'point')} par message, au plus un toutes les ${r.delaiEntreMessagesS} s — ${parHeure} points l’heure au mieux.`,
  });

  // 7. LE MINIMUM EN VOCAL — « pas de points pour une fenêtre laissée ouverte ».
  lignes.push({
    cle: 'minimum', minimum: r.minimumEnVocal,
    dit: `Rien ne se gagne en vocal en dessous de ${pluriel(r.minimumEnVocal, 'personne')} dans le salon.`,
  });

  // 8. LES MULTIPLICATEURS, CHACUN CHIFFRÉ SUR UNE DEMI-HEURE.
  const base = r.parMinuteVocale * 30;
  if (!r.multiplicateurs.length) {
    lignes.push({
      cle: 'multiplicateurs', nombre: 0,
      dit: 'Aucun multiplicateur : tout le monde gagne au même rythme.',
    });
  } else {
    for (const m of r.multiplicateurs) {
      lignes.push({
        // ⚠️ LE FACTEUR REEL VIENT DE `facteurDe`, PAS DU CHIFFRE SAISI.
        //
        // Le reglage « 1,5 » n'est pas un multiplicateur : c'est un AJOUT a ×1.
        // Quelqu'un qui porte ce seul role est donc a ×2,5, et l'ecran
        // annoncait ×1,5 — la moitie du gain reel. On regle un role sur ce
        // qu'on lit ici : l'ecran doit calculer comme la caisse, jamais
        // reproduire le chiffre saisi.
        cle: `multiplicateur:${m.role}`,
        role: m.role,
        facteur: facteurDe([m.role], r.multiplicateurs),
        gain: appliquerFacteur(base, facteurDe([m.role], r.multiplicateurs)),
        base,
        dit: `${nomDuRole(m.role, roles)} : +${nombre(m.facteur)} — avec ce rôle seul on est à × ${nombre(facteurDe([m.role], r.multiplicateurs))}, et 30 minutes de vocal rapportent ${appliquerFacteur(base, facteurDe([m.role], r.multiplicateurs))} points au lieu de ${base}.`,
      });
    }
    // ⚠️ LES RÔLES S'ADDITIONNENT, ILS NE SE REMPLACENT PLUS.
    //
    // Toji, 14/09 : « en base on est en ×1 ; si on rajoute un rôle avec 1,5, ça
    // fait 2,5 — c'est de l'ajout à chaque fois ». Avant, on gardait le
    // meilleur, et un petit rôle ne servait à rien dès qu'on en avait un gros.
    //
    // L'écran est le seul endroit où on peut le dire avant qu'on le découvre
    // sur un gain — et il le dit avec le CALCUL, pas avec une phrase écrite à la
    // main qui redeviendrait fausse à la prochaine décision.
    if (r.multiplicateurs.length > 1) {
      const tous = r.multiplicateurs.map((m) => m.role);
      const cumule = facteurDe(tous, r.multiplicateurs);
      const plafond = 1 + MULTIPLICATEUR_MAX;
      lignes.push({
        cle: 'cumul',
        facteur: cumule,
        plafonne: cumule >= plafond,
        dit: cumule >= plafond
          ? `Quelqu’un qui porte tous ces rôles atteint le plafond : × ${nombre(plafond)}, et pas au-delà.`
          : `Quelqu’un qui porte tous ces rôles CUMULE : × ${nombre(cumule)} (1 + ${r.multiplicateurs.map((m) => nombre(m.facteur)).join(' + ')}), dans la limite de × ${nombre(plafond)}.`,
      });
    }
  }

  return lignes;
}
